import { ChangeDetectorRef, Component, HostListener, isDevMode, NgZone, OnDestroy, OnInit } from '@angular/core';
import { MessagingService } from './services/messaging.service';
import { Globals } from './globals';
import { AuthenticationService } from './modules/auth/services/authentication.service';
import { CurrentUser } from './modules/auth/model/CurrentUser';
import { Event, NavigationStart, Router } from '@angular/router';
import { PatientService } from './modules/patient/patient.service';
import { StaticDataService } from './services/static-data.service';
import { MatDialog } from '@angular/material/dialog';
import { UserService } from './services/user.service';
import { UiService } from './services/ui.service';
import { SignalrService } from './services/socket/signalr.service';
import { LocalStorageService } from './services/local-storage.service';
import { DialogService } from './services/dialog.service';
import { MessagingConstants } from './models/MessagingConstants';
import { RoleGuard } from './modules/auth/helpers/role-guard';
import { filter, tap } from 'rxjs/operators';
import { HomeAccordionService } from "./main-interface/home/controls/home-accordion/home-accordion.service";

@Component({
  standalone: false,
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css']
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'TDOS';
  currentUser: CurrentUser;

  patientSearchSubscription: any;
  showNotes: boolean = false;
  showNotifications: boolean = false;
  showLiveChat: boolean = false;
  showHeader: boolean = false;
  activityTimer: any;

  isPopupWindow: boolean = false;
  isLoading: boolean = false;
  messagingSubscription: any;

  private lastWindowsWidth: number = 0;
  private isWindowToSmallWarningActive = false;
  userMode: string;

  constructor(
    private cdr: ChangeDetectorRef,
    private messagingService: MessagingService,
    private router: Router,
    private authService: AuthenticationService,
    public globals: Globals,
    private patientService: PatientService,
    private staticDataService: StaticDataService,
    public dialog: MatDialog,
    private dialogService: DialogService,
    public uiService: UiService,
    private localStorageService: LocalStorageService,
    private signalrService: SignalrService,
    private homeAccordionService: HomeAccordionService,
    private ngZone: NgZone
  ) {
    this.authService.currentUser.subscribe(x => {
      this.currentUser = x;
    });

    this.signalrService.messageUser.subscribe((message) => {
      switch (message.messageType) {
        case MessagingConstants.USER_ROLES_CHANGED:
          RoleGuard.checkAccess(this.router.url);
          break;
        case MessagingConstants.PATIENT_CHANGE_STARTLEDGER:
          // if (this.router.url !== '/reports' && this.router.url !== '/checkout') {
          // Update globals BEFORE navigating so ledger picks up the correct patient on init
          this.globals.currentPatientId = message.message['PatientID'];
          const patientData = {
            PatientID: message.message['PatientID'],
            PatientName: message.message['PatientName'],
            clearCurrentQuery: false,
            Process: MessagingConstants.DO_NOT_LOAD_CASE_VIEW
          };
          if (this.router.url !== '/reports') {
            if (!this.router.url.includes('landing/ledger')) {
              // Not on ledger - navigate to it first, then broadcast
              this.router.navigateByUrl('landing/ledger').then(() => {
                this.messagingService.broadCastMessage(MessagingConstants.PATIENT_CHANGED, patientData);
              });
            } else {
              // Already on ledger - just broadcast, ledger will reload via PATIENT_CHANGED handler
              this.messagingService.broadCastMessage(MessagingConstants.PATIENT_CHANGED, patientData);
            }
          } else {
            // On reports page, just broadcast patient change without navigating
            this.messagingService.broadCastMessage(MessagingConstants.PATIENT_CHANGED, patientData);
          }
          break;
      }
    });

    // Session-restore guard: runs in the constructor (before the Angular router
    // starts its initial navigation) so AuthGuard never sees stale credentials.
    //
    // sessionStorage is cleared when the browser window/tab closes but survives
    // page refresh.  If a JWT token is present in localStorage but there is no
    // active-session flag in sessionStorage, the browser was closed while the
    // user was still logged in — force a logout so the restored tab always
    // starts from the login page.
    // console.log('AppComponent: constructor localStorage.getItem TDOauthToken', localStorage.getItem('TDOauthToken'));
    // console.log('AppComponent: constructor sessionStorage.getItem _appActiveSession', sessionStorage.getItem('_appActiveSession'));
    const hasToken = !!localStorage.getItem('TDOauthToken');
    const hasActiveSession = !!sessionStorage.getItem('_appActiveSession');
    // console.log('AppComponent: constructor hasToken', hasToken);
    // console.log('AppComponent: constructor hasActiveSession', hasActiveSession);
    if (hasToken && !hasActiveSession) {
      this.authService.logout(); // clears localStorage + navigates to /login
    } else if (UserService.authToken) {
      //console.log('AppComponent -> constructor -> UserService.authToken', UserService.authToken);
      this.refershDataServiceValues();
    }
    // console.log('AppComponent: constructor sessionStorage.setItem = 1');
    sessionStorage.setItem('_appActiveSession', '1');
  }

  private refershDataServiceValues() {
    //console.log('refershDataServiceValues this.authService.isLoggedIn()', this.authService.isLoggedIn());
    if (!this.staticDataService.isReady && this.authService.isLoggedIn()) {
      this.authService.currentPatient.subscribe(patientObj => {
        //console.log('refershDataServiceValues patientObj', patientObj);
        if (patientObj) {
          this.userMode = 'patient';
        }
        else
          this.userMode = '';
        //console.log('refershDataServiceValues userMode:', this.userMode);
        if (this.authService.isLoggedIn()) {
          // console.log('refershDataServiceValues refreshValues user logged in');
          // console.log('refershDataServiceValues forceRefresh: true');
          this.staticDataService.refreshValues(true, this.userMode != 'patient',true,true, true);
        }
      });
    }
  }

  @HostListener('window:resize', ['$event'])
  onResize(event) {
    if (this.router.url === '/notes' || this.router.url.startsWith('/patientportal') 
      || this.router.url.startsWith('/referrerportal') || this.router.url.startsWith('/patientLogin')
      || this.router.url.startsWith('/referrerLogin') || this.router.url.startsWith('/login')) {
      return;
    }
    if (this.lastWindowsWidth > window.innerWidth) {
      if (window.innerWidth < 1382 && !this.isWindowToSmallWarningActive) {
        this.isWindowToSmallWarningActive = true;
        this.dialogService.openConfirmDialog('This site is designed to be utilized in full screen.  Please increase window size.', false, false, 'Window Resolution Small')
        .subscribe((res) => {
          this.isWindowToSmallWarningActive = false;
        });
      }
    }

    if (window.innerWidth >= 1382 && this.isWindowToSmallWarningActive) {
      if (this.dialogService.confirmDialogRef) {
        this.isWindowToSmallWarningActive = false;
        this.dialogService.confirmDialogRef.close();
        this.dialogService.confirmDialogRef = null;
      }
    }

    this.lastWindowsWidth = window.innerWidth;
  }

  @HostListener('window:beforeunload', ['$event'])
  beforeunloadHandler(event) {
    localStorage.removeItem('handleTDO12TokenFlag');
    this.messagingService.alertChildrenOnClosure();
    this.messagingService.alertOpenerOnClosure();
    this.uiService.closePopupWindows();
  }

  returnImageDimensionsFromURL(url: string): number[] {
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    img.src = url;
    const dimensions: number[] = [img.height, img.width];
    return dimensions;
  }

  ngOnInit() {
    this.router.events.pipe(
      filter((event: Event) => event instanceof NavigationStart),
      tap(() => this.dialog.closeAll())
    ).subscribe();

    // this.tokenTesting();

    this.router.events.subscribe((data) => {
      if (data instanceof NavigationStart) {
        if (data.url.includes('referralPortal')) {
          this.isPopupWindow = true;
        }
        else if (data.url === '/landing') {
          this.router.navigateByUrl('landing/case-view');
        }
        else if (data.url === '/patientportal/appointment/print') {
          this.showHeader = false;
        }
        else if (data.url.startsWith('/patientLogin') || data.url.startsWith('/login')) {
          this.showHeader = false;
        }
        else if (data.url.includes('/patientportal')) {
          this.showHeader = true;
        }
        else if (data.url.startsWith('/payment-receipt')) {
          this.showHeader = false;
        }
        else if (data.url.startsWith('/pl/') || data.url.startsWith('/rl/')) {
          // Anonymous magic-link landing pages (patient/referrer document links) - no session
          // exists yet here, so showing the header would let TdoHeaderComponent's
          // "userMode === '' -> auto logout + redirect to /login" safety net fire before (or
          // instead of) the auto-login flow ever gets a chance to run.
          this.showHeader = false;
        }
        else if (data.url.startsWith('/pay/')) {
          this.showHeader = false;
        }
        else if (data.url.startsWith('/collage')) {
          this.showHeader = false;
        }
        else
          this.showHeader = true;
      }
    });
    // if (this.authService.isLoggedIn()) {
    this.globals.load();
    // currentUserData reads 'storedUser', which storeReferringDoctorTokenResult/
    // storePatientTokenResult also set (alongside storedReferrerUser/storedPatient) - without
    // this exclusion, referrer/patient portal sessions try to open the staff SignalR hub
    // connection, which their token isn't meant for and which then just hangs indefinitely
    // (pending websocket, retried on a loop).
    // Also checks the URL directly (not just the isReferrerRole/isPatientRole localStorage
    // flags) - on the very first load of a /rl/ or /pl/ magic link, auto-login hasn't completed
    // yet, so those flags don't exist, but we're just as much a referrer/patient context.
    const isReferrerOrPatientSession = localStorage.getItem('isReferrerRole') === 'true' || localStorage.getItem('isPatientRole') === 'true'
      || window.location.pathname.startsWith('/rl/') || window.location.pathname.startsWith('/pl/');
    if (this.localStorageService.currentUserData && !isReferrerOrPatientSession && !(window.opener && window.location.pathname.startsWith('/reports'))) {
      this.signalrService.connect();
    }
    if (!isReferrerOrPatientSession) {
      this.signalrService.connectToBroker();
    }


    this.messagingService.init();
    this.messagingSubscription = this.messagingService.componentMessageCall
      .subscribe(response => {
        const event = response.event;
        const eventData = response.data;

        if (event === 'Window-is-Popup') {
          this.isPopupWindow = true;
        }

        if (event === 'Popup-has-opened') {
          // if this module handles a pop-up - do something
        }

        if (event === 'Toggle-Notification-Display') {
          if (eventData.hide) {
            this.showNotifications = false;
          } else {
            this.showNotifications = !this.showNotifications;
          }
        }

        if (event === 'ALERT_MESSAGES_COUNT') {
          if (!this.showLiveChat) {
            this.messagingService.sendComponentMessage('UPDATE_MESSAGES_COUNT', { count: eventData.count });
          }
        }

        if (event === 'Toggle-Live-Chat-Display') {
          if (eventData.hide) {
            this.showLiveChat = false;
          } else {
            this.showLiveChat = !this.showLiveChat;
          }

          if (this.showLiveChat) {
            this.messagingService.sendComponentMessage('UPDATE_MESSAGES_COUNT', { count: 0 });
          }
        }

        if (event === 'Toggle-Notes-Display') {
          this.showNotes = eventData.display;
        }
        if (event === MessagingConstants.PATIENT_CHANGE_STARTLEDGER) {
          // start ledger
          this.router.navigateByUrl('landing/ledger');
          // switch patient
          this.messagingService.broadCastMessage(MessagingConstants.PATIENT_CHANGED, {
            PatientID: eventData.PatientId,
            PatientName: eventData.dataContact.FirstName + ' ' + eventData.dataContact.MidName + ' ' + eventData.dataContact.LastName,
            clearCurrentQuery: false,
            Process: MessagingConstants.DO_NOT_LOAD_CASE_VIEW
          });
        }
        if (event === MessagingConstants.VALID_VALUES_REFRESH) {
          // Refresh Valid Values
          // console.log('event VALID_VALUES_REFRESH');
          this.refershDataServiceValues();

        }
        if (event === MessagingConstants.PATIENT_CHANGED) {
          // console.log('event PATIENT_CHANGED: ', response);
          localStorage.setItem('isSelectedMTI', 'false');
          //localStorage.setItem('isShowModuleSpecificNotes', 'true'); keep setting selected by user
          this.uiService.handlePatientSwitchImagingWindow();
          this.globals.ThePatient = null;
          this.globals.currentPatientFullName = '';
          this.globals.currentPatientCaseId = null;
          this.globals.currentParentPatientCaseId = null;
          this.globals.save();

          if (eventData.PatientID) {
            eventData.PatientId = eventData.PatientID;
          }

          if (this.patientSearchSubscription) {
            this.patientSearchSubscription.unsubscribe();
            this.patientSearchSubscription = false;
          }
          if (eventData.PatientId != 'undefined' && eventData.PatientId != undefined && eventData.PatientId !== 'null' && eventData.PatientId !== null) {

            this.patientSearchSubscription = this.patientService.getPatient(eventData.PatientId)
              .subscribe(patientResponse => {
                this.patientSearchSubscription = false;
                const res = patientResponse;

                if (res?.ThePatient) {
                  this.globals.ThePatient = res.ThePatient;

                  this.globals.currentPatientId = res.ThePatient.PatientId;
                  if (res.ThePatient.dataContact) {
                    const firstName = res.ThePatient.dataContact.FirstName || '';
                    const midName = res.ThePatient.dataContact.MidName || '';
                    const lastName = res.ThePatient.dataContact.LastName || '';
                    this.globals.currentPatientFullName = `${firstName} ${midName} ${lastName}`;
                    this.globals.currentPatientNo = res.ThePatient.PatientNO != null ? `${res.ThePatient.PatientNO}` : '';
                    if (eventData.Process === MessagingConstants.DO_NOT_LOAD_CASE_VIEW) { // Load Patient without redirecting to  case view
                      this.messagingService.recursiveBroadCastMessage(MessagingConstants.PATIENT_LOADED, {
                        PatientID: this.globals.currentPatientId,
                        Process: MessagingConstants.DO_NOT_LOAD_CASE_VIEW,
                      });
                    }
                    else {
                      this.messagingService.recursiveBroadCastMessage(MessagingConstants.PATIENT_LOADED, this.globals.currentPatientId);
                    }
                  }
                }
                if (eventData.Process) {
                  switch (eventData.Process) {
                    case MessagingConstants.CHECKOUT_PATIENT_START:  // Check out
                      this.globals.currentPatientCaseId = eventData.PatientCaseId;
                      this.globals.currentParentPatientCaseId = eventData.ParentPatientCaseId;
                      this.messagingService.broadCastMessage(MessagingConstants.CHECKOUT_PATIENT_START, {});
                      break;
                    case MessagingConstants.RELOAD_SCHEDULE_APPOINTMENTS:  // Reload schedule appointments
                      this.homeAccordionService.loadScheduleAppointments();
                      break;
                    default:

                    // code block
                  }
                }
                this.globals.save();
              }, err => {
                this.patientSearchSubscription = false;
              });
          }
          this.uiService.handlePatientSwitchImagingWindow();
        }
      });
      // console.log('AppComponent -> ngOnInit -> this.showHeader', this.showHeader);

  }

  deleteKey(jsonObj, path): any {
    const obj = JSON.parse(JSON.stringify(jsonObj));
    const keys = path.split('.');

    keys.reduce((acc, key, index) => {
      if (key === '') {
        return acc;
      }

      if (index === keys.length - 1) {

        delete acc[key];
        return true;

      }
      return acc[key];
    }, obj);

    return obj;
  }

  initActivityMonitor() {
    if (this.router.url.includes('/referralPortal') || this.router.url.includes('/login')
      || this.router.url.includes('/pl/') || this.router.url.includes('/rl/') || this.router.url.includes('/patientLogin')
      || this.router.url.startsWith('/pay/')){
      return;
    }
    if (window.opener){
      window.opener.postMessage('initActivityMonitor');
      return;
    }

    const token = localStorage.getItem('TDOauthToken');
    const AppUITimeoutMinutes = localStorage.getItem('AppUITimeoutMinutes')??"60";
    // 60 minute inactivity check.
    if (token && typeof token !== undefined) {
      this.activityTimer = setTimeout(_ => {
        // console.log('initActivityMonitor logout');
        this.authService.logout();
        this.messagingService.alertChildrenOnClosure();
        this.uiService.closePopupWindows();
        if (this.router.url.includes('/scheduler') || this.router.url.includes('/configuration') || this.router.url.includes('/forms/builder')
          || this.router.url.includes('/studio') || this.router.url.includes('/reports') || this.router.url.includes('/appointment/print')
          || this.router.url.includes('/collage'))
          window.close();
        else if (this.router.url.includes('/patientportal'))
          this.router.navigateByUrl(`/patientLogin`);
        //else if (this.router.url.includes('/referrerportal'))
          // this.router.navigateByUrl(`/referrerLogin`);
        else if (!this.router.url.includes('/patientLogin') 
          && !this.router.url.includes('/referrerLogin') 
          && !this.router.url.includes('/login')
          // && !this.router.url.includes('/referralPortal')
        ){
          this.router.navigate(['/login']);
        }
      }, 1000 * 60 * Number(AppUITimeoutMinutes));
    }
    else { //No token found
      this.messagingService.alertChildrenOnClosure();
      this.uiService.closePopupWindows();
      if (this.router.url.includes('/scheduler') || this.router.url.includes('/configuration') || this.router.url.includes('/forms/builder') 
        || this.router.url.includes('/studio') || this.router.url.includes('/reports') || this.router.url.includes('/collage'))
        window.close();
      else if (this.router.url.includes('/patientportal'))
        this.router.navigateByUrl(`/patientLogin`);
      else if (this.router.url.includes('/referrerportal'))
        this.router.navigateByUrl(`/referrerLogin`);
      else if (!this.router.url.startsWith('/pl/') && !this.router.url.startsWith('/rl/') && !this.router.url.startsWith('/pay/') && !this.router.url.includes('/patientLogin') && !this.router.url.includes('/referrerLogin') && this.router.url !== '/login'){
        // console.log(`initActivityMonitor timeout ${AppUITimeoutMinutes} minutes, navigate to login -> this.router.url`, this.router.url);
        this.ngZone.run(() => { this.router.navigate(['/login']); });
      }
    }
  }

  @HostListener('window:mousemove') clearActivityTimer() {
    if (this.activityTimer) {
      clearTimeout(this.activityTimer);
    }
    this.initActivityMonitor();

  }

  ngOnDestroy() {
    this.messagingSubscription.unsubscribe();
  }

  @HostListener('window:message', ['$event'])
  onMessage(event: MessageEvent) {
    if (event.isTrusted && event.data === 'initActivityMonitor') {
      this.clearActivityTimer();
    }
    if (event.origin === window.location.origin && !window.opener &&
        event.data?.messageType === MessagingConstants.PATIENT_CHANGE_STARTLEDGER) {
      this.globals.currentPatientId = event.data.PatientID;
      const patientData = {
        PatientID: event.data.PatientID,
        PatientName: event.data.PatientName,
        clearCurrentQuery: false,
        Process: MessagingConstants.DO_NOT_LOAD_CASE_VIEW
      };
      if (!this.router.url.includes('landing/ledger')) {
        this.router.navigateByUrl('landing/ledger').then(() => {
          this.messagingService.broadCastMessage(MessagingConstants.PATIENT_CHANGED, patientData);
        });
      } else {
        this.messagingService.broadCastMessage(MessagingConstants.PATIENT_CHANGED, patientData);
      }
    }
  }
}
